/**
 * <s-copy-code> — copies a discount code to the visitor's clipboard.
 *
 * A button with commandfor = this element's id and command="--copy" triggers the copy.
 *
 * Required markup:
 *   [data-code] on the host            — the code to copy.
 *   [data-copy-status]                 — live region that briefly shows the confirmation.
 *   template[data-copied-text]         — translated confirmation text.
 */
class SCopyCode extends HTMLElement {
  #ac;
  #timer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #handleCommand = async (event) => {
    if (event.command !== '--copy') return;

    const code = this.dataset.code?.trim();
    if (!code) return;

    const copied = await this.#copy(code);
    if (copied) this.#showStatus();
  };

  async #copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback for browsers or contexts without the async clipboard API.
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      this.append(field);
      field.select();
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
      field.remove();
      return ok;
    }
  }

  #showStatus() {
    const status = this.querySelector('[data-copy-status]');
    const template = this.querySelector('template[data-copied-text]');
    if (!status || !(template instanceof HTMLTemplateElement)) return;

    status.textContent = template.content.textContent?.trim() ?? '';
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      status.textContent = '';
    }, 2500);
  }
}

if (!customElements.get('s-copy-code')) {
  customElements.define('s-copy-code', SCopyCode);
}
